import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MessageIcon from "@mui/icons-material/Message";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { useEffect, useState } from "react";
import NavbarMonitoring from "./NavbarMonitoring";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { AppColors } from "resources/AppColors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserDoctor } from "@fortawesome/free-solid-svg-icons";
import { faWpforms } from "@fortawesome/free-brands-svg-icons";
import TableChartIcon from "@mui/icons-material/TableChart";
import { CommonTexts, scheduledForms } from "locale/es";
import { useDispatch, useSelector } from "react-redux";
import { getUnreadAlertsAction } from "redux/reducers/alertsReducer";
import StoreIcon from "@mui/icons-material/Store";
import { RolesManager } from "utils/RolesManager";
import PendingActionsIcon from '@mui/icons-material/PendingActions';

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: 1000,
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// * For the alerts section which is currently disabled since there is no support from backend
// const StyledBadge = styled(Badge)(({ theme }) => ({
//   "& .MuiBadge-badge": {
//     right: -3,
//     top: 13,
//     border: `2px solid ${theme.palette.background.paper}`,
//     padding: "0 4px",
//   },
// }));

const drawerWidth = 240;

const SideBar = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  const { user } = useSelector(state => state.userAuthentication)

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const studiesToken = user.tokens.studiesToken

  const isAdmin = RolesManager.userIsAdmin(user.roles);
  const isSuperAdmin = RolesManager.userIsSuperAdmin(user.roles);
  const isProfessional = RolesManager.userIsProfessional(user.roles);
  const isContentCreator = RolesManager.userIsContentCreator(user.roles);

  const selectedCenter = useSelector(({ center }) => center)?.data;

  // * For the alerts section which is currently disabled since there is no support from backend
  // const { unreadAlerts } = useSelector(({ alerts }) => alerts)

  const isHealthDataService = selectedCenter?.configuration?.healthDataService ?? true;
  const isNoteService = selectedCenter?.configuration?.noteService ?? true;
  const isMessageService = selectedCenter?.configuration?.messageService ?? true;
  const isContentService = selectedCenter?.configuration?.contentService ?? true;
  const isFormService = selectedCenter?.configuration?.formService ?? true;


  useEffect(() => {
    if (location.pathname === "/dades-del-participant") setSelectedSection(0);
    else if (location.pathname === "/notes") setSelectedSection(1);
    // else if (location.pathname === "/alertes") setSelectedSection(2)
    else if (location.pathname === "/missatges") setSelectedSection(2);
    else if (location.pathname === "/study") setSelectedSection(3);
    else if (location.pathname === "/contingut/management")
      setSelectedSection(4);
    else if (location.pathname === "/contingut/patients") setSelectedSection(4);
    else if (location.pathname === "/gestio-de-professionals")
      setSelectedSection(5);
    else if (location.pathname === "/gestio-dels-pacients")
      setSelectedSection(6);
    else if (location.pathname === "/configuracio-formularis")
      setSelectedSection(7);
      else if (location.pathname === "/programar-formularis")
      setSelectedSection(8);
    else if (
      location.pathname === `/gestio-dels-centres/${selectedCenter?.id}/fichas-de-centro`
    )
      setSelectedSection(9);
    // else if (location.pathname === "/componentes/formularios") setSelectedSection(9)
    // else if (location.pathname === "/componentes/contenidos") setSelectedSection(9)
    // else if (location.pathname === "/componentes/mensajes") setSelectedSection(9)
    else setSelectedSection(null);
  }, [location]);

  useEffect(() => {
    if (selectedCenter?.id && isProfessional) {
      dispatch(getUnreadAlertsAction(selectedCenter?.id));
    }
  }, [selectedCenter]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // TODO: Create icons styling class
  const sections = [
    {
      title: CommonTexts.PARTICIPANT_DATA,
      icon: (
        <MonitorHeartIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='charts'
        />
      ),
      link: "/dades-del-participant",
      isProfessionalRoute: true,
      shouldRender: isHealthDataService,
    },
    {
      title: CommonTexts.NOTES,
      icon: (
        <TextSnippetIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='notes'
        />
      ),
      link: "/notes",
      isProfessionalRoute: true,
      shouldRender: isNoteService,
    },
    // * For the alerts section which is currently disabled since there is no support from backend
    // {
    //   title: CommonTexts.ALERTS,
    //   icon:
    //     <IconButton aria-label="alerts">
    //       <StyledBadge badgeContent={unreadAlerts} color="secondary">
    //         <ReportProblemIcon sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }} data-cy="alerts" />
    //       </StyledBadge>
    //     </IconButton>
    //   ,
    //   link: '/alertes',
    //   isProfessionalRoute: true
    // },
    {
      title: CommonTexts.MESSAGES,
      icon: (
        <MessageIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='messages'
        />
      ),
      link: "/missatges",
      isProfessionalRoute: true,
      shouldRender: isMessageService
    },
    {
      title: CommonTexts.STUDY,
      icon: (
        <TableChartIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='study'
        />
      ),
      link: "/study",
      isProfessionalRoute: true,
      shouldRender: true,
    },
    {
      title: CommonTexts.CONTENT,
      icon: (
        <CreateNewFolderIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='content'
        />
      ),
      link: "/contingut/management" || "/contingut/patients",
      isContentCreatorRoute: true,
      shouldRender: isContentService,
    },
    {
      title: CommonTexts.PROFESSIONALS_MANAGEMENT,
      icon: (
        <FontAwesomeIcon
          icon={faUserDoctor}
          size='xl'
          fixedWidth
          style={{ color: AppColors.PRIMARY }}
          data-cy='professionals'
        />
      ),
      link: "/gestio-de-professionals",
      isAdminRoute: true,
      shouldRender: true,
    },
    {
      title: CommonTexts.PATIENTS_MANAGEMENT,
      icon: (
        <PersonAddIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='patients'
        />
      ),
      link: "/gestio-dels-pacients",
      isProfessionalRoute: true,
      shouldRender: true,
    },
    {
      title: CommonTexts.FORMS,
      icon: (
        <FontAwesomeIcon
          icon={faWpforms}
          size='xl'
          fixedWidth
          style={{ color: AppColors.PRIMARY }}
          data-cy='config'
        />
      ),
      link: "/configuracio-formularis",
      isAdminRoute: true,
      shouldRender: isFormService,
    },
    {
      title: scheduledForms.TITLE,
      icon: (
        <PendingActionsIcon
        sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
        data-cy='scheduledform'
        />
      ),
      link: "/programar-formularis",
      isSuperAdminRoute: true,
      shouldRender: isFormService,
    },
    {
      title: "Ficha de centro",
      icon: (
        <StoreIcon
          sx={{ fill: AppColors.PRIMARY, width: "28.8px", height: "36px" }}
          data-cy='patients'
        />
      ),
      link: `gestio-dels-centres/${selectedCenter?.id}/fichas-de-centro`,
      isAdminRoute: true,
      shouldRender: true,
    },
  ];

  // * If role is user don't show admin links
  const drawer = (
    <div>
      {sections.map((section, i) => {
        if (
          section.link === "/study" &&
          (studiesToken === "null" || !selectedCenter?.studyId)
        ) {
          return null;
        }

        if (
          (section.isAdminRoute && !isAdmin) ||
          (section.isProfessionalRoute && !isProfessional) ||
          (section.isContentCreatorRoute && !isContentCreator) ||
          (section.isSuperAdminRoute && !isSuperAdmin) ||
          (!section.shouldRender)
        ) {
          return null;
        }

        return (
          <ListItem
            key={section.title}
            disablePadding
            sx={{ display: "block" }}
          >
            <ListItemButton
              onClick={() => {
                if (
                  section.link ===
                  `gestio-dels-centres/${selectedCenter.id}/fichas-de-centro`
                ) {
                  return navigate(section.link, {
                    state: { entity: selectedCenter },
                  });
                } else {
                  navigate(section.link);
                }
              }}
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
                bgcolor: selectedSection === i ? "#0000003d" : "",
                "&:hover": {
                  bgcolor: "#00000024)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                {section.icon}
              </ListItemIcon>
              <ListItemText sx={{ opacity: open ? 1 : 0 }}>
                <Typography variant='body_drawer'>{section.title}</Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      })}
    </div>
  );

  // TODO: Fix properly (Due to funny stuff that the study table is doing when loaded with data)
  const calcDrawerWidth = open
    ? `calc(100% - ${drawerWidth}px)`
    : `calc(100% - 65px)`;

  /* //** Since the navbar and the other components are rendered as children of sidebar,
     //** if the route is /login it returns the navbar and the children to render the app without the sidebar
  */
  return (
    <>
      {location.pathname !== "/login" ? (
        <Box
          id='drawer'
          sx={{
            display: "flex",
            width: location.pathname === "/study" ? calcDrawerWidth : "100%",
          }}
        >
          <CssBaseline />
          <AppBar position='fixed' open={open}>
            {/* // TODO: Move opening and closing logic to this component instead of having it in the navbar */}
            <NavbarMonitoring
              logged={true}
              open={open}
              handleDrawerClose={handleDrawerClose}
              handleDrawerOpen={handleDrawerOpen}
            />
          </AppBar>
          <Drawer variant='permanent' open={open}>
            <List sx={{ mt: 8 }}>{drawer}</List>
          </Drawer>
          {children}
        </Box>
      ) : (
        <>
          <NavbarMonitoring logged={false} />
          {children}
        </>
      )}
    </>
  );
};

SideBar.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

export default SideBar;
